import React from "react";
import { LegalHero, LegalBody, H2, LastUpdated } from "./legalHelpers";
import ObfuscatedEmail from "../components/ObfuscatedEmail";
import { usePageMeta } from "../components/Layout";

export default function PrivacyPage() {
    usePageMeta({
        title: "Privacy Policy | London Renovation Specialists",
        description: "How we handle information you share with us through our free-design enquiry service.",
    });
    return (
        <div data-testid="privacy-page">
            <LegalHero
                eyebrow="Privacy"
                title="Privacy Policy"
                intro="How we handle information you share with us through our free-design enquiry service."
            />
            <LegalBody>
                <LastUpdated />

                <H2>Who we are</H2>
                <p>
                    This website is operated by London Renovation Specialists, a trading name of{" "}
                    Studio G4 Ltd, a company registered in England and Wales
                    under company number 12308705, with registered office at{" "}
                    10 Barley Mow Passage, Chiswick, London, W4 4PH.
                </p>
                <p>
                    For any privacy-related questions, you can contact us at{" "}
                    <ObfuscatedEmail user="hello" domain="londonrenovationspecialists.com" className="underline hover:text-terracotta" />.
                </p>

                <H2>What information we collect</H2>
                <p>
                    When you submit a free-design enquiry through this website, we collect the information you
                    provide, which typically includes:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>your name;</li>
                    <li>your email address;</li>
                    <li>your telephone number;</li>
                    <li>the postcode of the property;</li>
                    <li>your relationship to the property (owner, recently purchased, or in the process of purchasing);</li>
                    <li>the type of project you are considering;</li>
                    <li>a short description of what you are hoping to achieve;</li>
                    <li>your anticipated construction budget;</li>
                    <li>your preferred construction timing.</li>
                </ul>
                <p>
                    We may also automatically capture basic technical information about how you arrived at our
                    website (for example, whether you came from an advertising campaign) so that we can understand
                    which channels reach the people we help. We do not use behavioural advertising trackers or
                    third-party analytics cookies.
                </p>

                <H2>Why we collect it</H2>
                <p>We collect this information only to:</p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>assess your renovation enquiry;</li>
                    <li>consider project feasibility;</li>
                    <li>contact you to discuss your project;</li>
                    <li>manage the free design service if you go ahead;</li>
                    <li>prepare proposals or quotations where appropriate;</li>
                    <li>manage the resulting client relationship if you appoint us.</li>
                </ul>
                <p>
                    We do not operate a general marketing programme against enquiry data. We do not sell your
                    information. We do not add you to a mailing list.
                </p>

                <H2>How long we keep it</H2>
                <p>
                    If we do not proceed with your enquiry, we will normally delete your enquiry data once the
                    enquiry is concluded, subject to any reasonable short period necessary for administration or
                    legal purposes.
                </p>
                <p>
                    If you go on to work with us, we retain the information required to deliver your project, keep
                    proper business records, and meet our legal and regulatory obligations.
                </p>

                <H2>Your rights</H2>
                <p>
                    Under UK data protection law you have the right to ask us for a copy of the information we hold
                    about you, to correct it if it is inaccurate, to ask us to delete it, and to object to certain
                    uses of it. You can exercise any of these rights by contacting us at{" "}
                    <ObfuscatedEmail user="hello" domain="londonrenovationspecialists.com" className="underline hover:text-terracotta" />.
                </p>
                <p>
                    You also have the right to complain to the UK Information Commissioner's Office
                    (<a className="text-terracotta underline" href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a>)
                    if you are unhappy with how we have handled your information.
                </p>

                <H2>Cookies</H2>
                <p>
                    This website uses only essential cookies required for the site to function correctly and be
                    administered securely. We do not use advertising, marketing or behavioural tracking cookies.
                    See our <a className="text-terracotta underline" href="/cookies">Cookie Notice</a> for details.
                </p>

                <H2>Changes to this policy</H2>
                <p>
                    We may update this policy from time to time. The "last updated" date at the top of this page
                    indicates when the current version was published.
                </p>
            </LegalBody>
        </div>
    );
}
